import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  StackContextManager,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web';

declare global {
  interface Window {
    // Page-level flag marking that OTel has already been initialized. Used to
    // guarantee setup runs at most once (see the guard in `initTracing`).
    __ND_OTEL_INITIALIZED__?: boolean;
  }
}

/**
 * Frontend tracing for the Hosting MFE — Phase 1 (traceparent propagation only).
 *
 * WHAT THIS DOES
 * On every outbound HUAPI/BFF request the browser makes, OpenTelemetry starts a
 * span and injects the W3C `traceparent` HTTP header. The backend reads that
 * header and attaches its own spans to the same trace, so a single request can
 * be followed browser -> BFF -> downstream services.
 *
 * The `traceparent` header looks like:
 *   traceparent: 00-4bf92f3577b34da6a3ce929d0e0e4736-00f067aa0ba902b7-01
 *                └┬┘ └──────────────┬─────────────┘ └───────┬──────┘ └┬┘
 *              version           trace-id (16B)      parent span-id(8B) flags
 * Spec: https://www.w3.org/TR/trace-context/#traceparent-header
 *
 * PHASE 1 SCOPE (current)
 * Propagate `traceparent` only — we do NOT export spans anywhere yet. There is
 * no exporter and no collector endpoint; the browser just stamps the header so
 * backends can correlate. Exporting spans to an OTLP collector (SigNoz) is
 * Phase 2 and will be added later.
 *
 * HOW IT LOADS
 * This is a side-effect import (`import './instrumentation'`). Importing the
 * module runs `initTracing()`. It is imported from `src/Init.tsx`, which sits on
 * both of the MFE's consumption paths (the `window.Hosting.init` bundle and the
 * Module Federation `./Init` expose), so tracing is set up no matter how the MFE
 * is loaded. It must be imported before app code so requests are instrumented
 * from the very first call.
 *
 * References:
 *  - OTel JS (web): https://opentelemetry.io/docs/languages/js/getting-started/browser/
 *  - Context propagation: https://opentelemetry.io/docs/concepts/context-propagation/
 */

// Only inject `traceparent` on requests to first-party Newfold backends over
// HTTPS (HUAPI `*.uapi.newfold.com` and BFF `*.hosting.newfold.com`).
//
// Why anchored to `^https://...newfold.com/` instead of a loose substring:
//  - Prevents leaking our trace ids to third parties if a URL merely *contains*
//    "newfold.com" somewhere (e.g. in a query string).
//  - These are cross-origin calls from the app, so the browser sends a CORS
//    preflight; the target server must allow the header via
//    `Access-Control-Allow-Headers: traceparent` for the request to go through.
//    Docs: https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS
const NEWFOLD_HOSTS = /^https:\/\/([a-z0-9-]+\.)*newfold\.com(?::\d+)?\//i;

const initTracing = () => {
  // No `window` means we're not in a browser (e.g. SSR / tests) — nothing to do.
  if (typeof window === 'undefined') return;

  // Run setup at most once per page. The MFE can be initialized more than once
  // (multiple mounts, or both consumption paths loaded together in separate
  // webpack runtimes); without this guard we'd double-patch fetch/XHR and emit
  // duplicate spans / duplicate `traceparent` headers.
  if (window.__ND_OTEL_INITIALIZED__) return;
  window.__ND_OTEL_INITIALIZED__ = true;

  try {
    // The provider is the root of the tracing SDK. `service.name` is the label a
    // backend uses to identify who created the span (this MFE).
    const provider = new WebTracerProvider({
      resource: resourceFromAttributes({
        'service.name':
          process.env.REACT_APP_OTEL_SERVICE_NAME || 'am-website-hosting-ui-bh',
      }),
    });

    // register() installs the W3C trace-context propagator (which writes the
    // `traceparent` header) and the stack-based context manager.
    provider.register({ contextManager: new StackContextManager() });

    // These instrumentations auto-wrap the browser's two HTTP mechanisms. For
    // each request whose URL matches NEWFOLD_HOSTS they create a span and inject
    // the `traceparent` header.
    registerInstrumentations({
      instrumentations: [
        // Covers native `fetch()` calls.
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: NEWFOLD_HOSTS,
        }),
        // Covers XMLHttpRequest. huapi-js uses axios, which in the browser runs
        // on XMLHttpRequest, so this is what stamps `traceparent` on our actual
        // HUAPI/BFF calls in Phase 1.
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls: NEWFOLD_HOSTS,
        }),
      ],
    });
  } catch (err) {
    // Tracing is best-effort observability — it must never break app bootstrap.
    // Swallow any init error and let the app render normally.
    console.warn('[otel] tracing initialization failed', err);
  }
};

initTracing();
